import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import { ThemeProvider } from '@mui/material/styles';
import { createDebouncedUpdate, updateCellProperty, updateOutageEntry } from '../utils/InspectorUtils';

// map of codes → human labels
const labelTextMap = {
  BE: `Banque d'enregistrement`,
  MEB: 'Mise en bac',
  PIM: `Poste d'identification manuel`,
  CP: 'Contrôle primaire',
  CC: 'Contrôle complémentaire',
  CU: 'Contrôle ultime',
  CONV: 'Convergence',
  DIV: 'Divergence',
  STOCK: 'Stock',
  ECH: 'Échangeur de technologie',
  DEB: 'Débacage',
  PFT: 'Position finale de tri',
  DM: 'Sortie dernière minute',
  REBUT: 'Rebut',
  TRI: 'Trieur',
  DC: 'Dépose correspondance',
};

function TextInputFieldComponent({ field, cell, theme }) {
  // initial value from the cell (or fallback)
  const initial = cell.get(field.path) ?? field.defaultValue ?? '';
  const initialValue = initial === undefined || initial === 'undefined' ? '' : initial;
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef(null);

  const cellId = field.cellId;
  const graph = cell.graph;

  // silent, debounced updates on every keystroke
  const debouncedSilentUpdate = useMemo(
    () =>
      createDebouncedUpdate((newValue) => {
        if (field.group === 'outages') {
          updateOutageEntry(graph, cellId, 0, field.path, newValue);
        } else {
          updateCellProperty(cell, field, field.path, newValue, { silent: true, dirty: true });
        }
      }, 300),
    [graph, cellId, field, cell]
  );

  // clean up the debouncer on unmount
  useEffect(() => {
    return () => debouncedSilentUpdate.cancel();
  }, [debouncedSilentUpdate]);

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    debouncedSilentUpdate(v);
  };

  const handleBlur = () => {
    debouncedSilentUpdate.cancel();

    if (field.group === 'outages') {
      updateOutageEntry(graph, cellId, 0, field.path, value);
    } else {
      updateCellProperty(cell, field, field.path, value);
      // ensure JointJS fires change events so inspector fully refreshes
      cell.trigger('change:attrs change:customAttributes', cell, {
        ...cell.attributes,
        [field.path]: value,
        dirty: true,
      });
    }
  };

  const displayValue = field.path === 'name' ? (labelTextMap[value] ?? value) : value;
  const safeDisplayValue = displayValue === undefined || displayValue === 'undefined' ? '' : displayValue;

  return (
    <ThemeProvider theme={theme}>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label={field.label}
        value={safeDisplayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={field.readOnly}
        required={field.required}
        error={field.required && !value}
        helperText={field.required && !value ? 'Ce champ est requis' : ''}
        inputRef={inputRef}
        inputProps={{
          'data-cy': field.attributes?.['data-cy'],
          onCompositionStart: (e) => e.stopPropagation(),
          onCompositionEnd: (e) => e.stopPropagation(),
        }}
      />
    </ThemeProvider>
  );
}

TextInputFieldComponent.propTypes = {
  field: PropTypes.shape({
    path: PropTypes.string.isRequired,
    defaultValue: PropTypes.string,
    cellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    group: PropTypes.string,
    label: PropTypes.string,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    attributes: PropTypes.object,
  }).isRequired,
  cell: PropTypes.shape({
    get: PropTypes.func.isRequired,
    graph: PropTypes.object.isRequired,
    trigger: PropTypes.func.isRequired,
    attributes: PropTypes.object.isRequired,
  }).isRequired,
  theme: PropTypes.object.isRequired,
};

export function renderTextInput(field, cell, theme) {
  const container = document.createElement('div');
  ReactDOM.render(<TextInputFieldComponent field={field} cell={cell} theme={theme} />, container);
  return container;
}

renderTextInput.propTypes = {
  field: PropTypes.object.isRequired,
  cell: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
};
